import * as React from 'react';
import { FilteredMarketGroupsOverview, FilteredMatchesWithEvents, MatchListStoreModel } from '../match-list/utils/match-list-models';
import { MATCHLIST_GROUP_BEM as bem, MatchListGroupData } from './match-list-group.constants';
import { DisciplineRouteType, getDisciplineRouteType } from '@om-fe/core/components/discipline-anchor/discipline-route.utils';
import TournamentAnchor from '@om-fe/core/components/tournament-anchor/tournament-anchor';
import DisciplineAnchor from '@om-fe/core/components/discipline-anchor/discipline-anchor';
import { connect } from 'react-redux';
import { AppState } from '@om-fe/core/core/types';
import getSlice from '@om-fe/core/utils/get-slice';
import getConfig, { hasUpcomingEvents, MarketsHeaderOptions } from '@om-fe/core/utils/config';
import { FavoriteTournamentsButton } from '@om-fe/core/components/favorites/favorite.utils';
import { AggregatorDisciplineModel, ModelTypes } from '@om-fe/models';
import { FlagIcon, FlagIconLocations } from '@om-fe/widgets/dist/components/icon/flag-icon';
import { GeneralIcon } from '@om-fe/widgets/dist/components/icon/general-icon';
import OutcomeList from '../match-list/markets-headers/outcome-list';
import MatchListSelector from '../match-list/match-list-selector';
import { PlatformTypes } from '@om-fe/utils';

interface MatchListGroupHeaderDefaultProps {
    readonly icons: {
        readonly iconIsCollapsed: string;
        readonly iconIsExpanded: string;
    };
}

export type MatchListGroupHeaderPublicProps = {
    readonly sliceKey: string;
    readonly locationId: string | undefined;
    readonly categoryId: string | undefined;
    readonly isExpanded: boolean;
    readonly groupData?: MatchListGroupData;
    readonly options?: MarketsHeaderOptions;
    readonly showMarketsName?: boolean;
    readonly toggle: () => void;
    readonly filteredMainMarketsList: string[];
    readonly filteredMainOutrightsMarketsList: string[];
    readonly showMarketInMatchListGroupHeader: boolean;
    readonly mainMarketsLimit: number;
    readonly groupOverview?: string;
    readonly selectGroupsOverview?: (item: string) => void;
    readonly filteredMarketGroupsOverviewList?: string[];
    readonly filteredMarketGroupsOverview?: Record<string, FilteredMarketGroupsOverview>;
    readonly isHasDrawer?: boolean;
    readonly deviceType?: PlatformTypes;
    readonly showEventCategory?: boolean;
    readonly preventRedirect?: boolean;
    readonly isVirtual?: boolean;
} & Partial<MatchListGroupHeaderDefaultProps>;

interface MatchListGroupHeaderStoreProps {
    readonly discipline: AggregatorDisciplineModel | undefined;
}

type MarketsHeaderProps = MatchListGroupHeaderPublicProps
    & MatchListGroupHeaderDefaultProps
    & MatchListGroupHeaderStoreProps;

class MatchListGroupHeaderComponent extends React.PureComponent<MarketsHeaderProps> {
    static defaultProps: MatchListGroupHeaderDefaultProps = {
        icons: {
            iconIsCollapsed: 'add',
            iconIsExpanded: 'remove',
        },
    };

    getLocationAnchorNode(): React.ReactNode {
        const { groupData } = this.props;

        const {
            shortSportName: disciplineName,
            sportId: disciplineId,
            venueName: locationName,
            venueId: locationId,
        } = groupData as FilteredMatchesWithEvents;

        return (
            <DisciplineAnchor
                disciplineRouteType={DisciplineRouteType.Location}
                disciplineId={disciplineId}
                disciplineName={disciplineName}
                childId={locationId}
                childName={locationName}
            >
                <span className={bem('Location')}>{locationName}</span>
            </DisciplineAnchor>
        );
    }

    getTournamentAnchorNode(): React.ReactNode {
        const { groupData, discipline, options, showEventCategory, preventRedirect } = this.props;

        if (!groupData) {
            return null;
        }

        const isHyphenatedFullUrl: boolean = !!getConfig().enableHyphenatedUrl?.fullName;
        const disciplineName = isHyphenatedFullUrl ? groupData?.sportName : groupData?.shortSportName;
        const tournamentName = isHyphenatedFullUrl ? groupData?.parentName : groupData?.shortParentName;

        const {
            sportId: disciplineId,
            parentName,
            parentId: tournamentId,
            venueId,
            venueName,
        } = groupData;

        let locationId: string | undefined;
        let locationName: string | undefined;
        let categoryId: string | undefined;
        let categoryName: string | undefined;

        // TODO: Find a better way of creating this as currently this does not make allot of sense, using two different data
        if (this.props.locationId) {
            locationId = venueId;
            locationName = venueName;
        } else if (this.props.categoryId) {
            categoryId = groupData.categoryId;
            categoryName = groupData.categoryName;
        } else if (discipline) {
            const disciplineRouteType: DisciplineRouteType = discipline.parentId
                ? DisciplineRouteType.Parent
                : getDisciplineRouteType(discipline);

            if (disciplineRouteType === DisciplineRouteType.Location) {
                locationId = venueId;
                locationName = venueName;
            } else if (disciplineRouteType === DisciplineRouteType.Category) {
                categoryId = groupData.categoryId;
                categoryName = groupData.categoryName;
            }
        }

        const countryCategory: boolean = (options === MarketsHeaderOptions.SHOW_COUNTRY && showEventCategory) ?? false;

        return (
            <TournamentAnchor
                disciplineId={disciplineId}
                disciplineName={disciplineName}
                tournamentId={tournamentId}
                tournamentName={parentName ?? tournamentName}
                locationId={locationId}
                locationName={locationName}
                categoryId={categoryId}
                categoryName={categoryName}
                preventRedirect={preventRedirect}
                className={bem('HeaderLink')}
            >
                <span className={bem('Tournament')}>{!countryCategory ? tournamentName : categoryName}</span>
                {!preventRedirect && <GeneralIcon name={'arr-right'} />}
            </TournamentAnchor>
        );
    }

    getTitleNode(): React.ReactNode {
        const { groupData, options, locationId, showEventCategory, isVirtual } = this.props;
        const {
            shortParentName: tournamentName,
            venueId,
        } = groupData as FilteredMatchesWithEvents;
        const flagIdentifier: string = venueId === FlagIconLocations.World && isVirtual ? FlagIconLocations.VirtualWorldIcon : venueId;

        if (options === MarketsHeaderOptions.SHOW_COUNTRY) {
            return (
                <>
                    <FlagIcon locationId={showEventCategory ? FlagIconLocations.World : flagIdentifier} />
                    {showEventCategory
                        ? this.getTournamentAnchorNode()
                        : this.getLocationAnchorNode()
                    }
                    <span className={bem('Separator')}> / </span>
                </>
            )
        }

        return (
            <>
                <FlagIcon locationId={venueId} />
                {
                    (options === MarketsHeaderOptions.SHOW_COUNTRY_AND_TOURNAMENT && (!locationId || locationId === 'ALL_LOCATION_ID')) && (
                        <>
                            {this.getLocationAnchorNode()}
                            <span className={bem('Separator')}> / </span>
                        </>
                    )
                }
                {
                    hasUpcomingEvents()
                        ?  (
                            this.getTournamentAnchorNode()
                        ) : (
                            <span className={bem('TitleText')}>{tournamentName}</span>
                        )
                }
            </>
        );
    }

    selectGroupItem = (item: string): void => {
        const { selectGroupsOverview } = this.props;

        if (!selectGroupsOverview) {
            return;
        }
        selectGroupsOverview(item);
    };

    outcomesContent(modelType: ModelTypes.TOURNAMENT | ModelTypes.MATCH): React.ReactNode {
        const {
            filteredMainMarketsList,
            filteredMainOutrightsMarketsList,
            sliceKey,
            deviceType,
            isHasDrawer,
            filteredMarketGroupsOverview,
            filteredMarketGroupsOverviewList,
            groupOverview,
            mainMarketsLimit,
            showMarketInMatchListGroupHeader,
        } = this.props;

        if (!showMarketInMatchListGroupHeader) {
            return null;
        }

        const isOutright: boolean = modelType === ModelTypes.TOURNAMENT;
        const customMainMarketsLimit: number = isOutright ? 1 : mainMarketsLimit;
        const mainMarketsList: string[] = isOutright ? filteredMainOutrightsMarketsList : filteredMainMarketsList

        if (filteredMarketGroupsOverviewList && modelType === ModelTypes.MATCH) {
            const children: React.ReactNode = <MatchListSelector
                selectGroupsOverview={this.selectGroupItem}
                filteredMarketGroupsOverview={filteredMarketGroupsOverview}
                filteredMarketGroupsOverviewList={filteredMarketGroupsOverviewList}
                marketSelected={groupOverview ? groupOverview : ''}
                mainMarketsLimit={mainMarketsLimit}
                isHasDrawer={isHasDrawer}
                deviceType={deviceType}
            />

            return mainMarketsList.map((mainMarketId: string,index: number) => (
                index !== filteredMainMarketsList.length - 1
                ? <OutcomeList key={mainMarketId} mainMarketId={mainMarketId} sliceKey={sliceKey} />
                : <OutcomeList key={mainMarketId} mainMarketId={mainMarketId} sliceKey={sliceKey} children={children} />
            ))
        }

        return mainMarketsList.map((mainMarketId: string, index: number) => (
            (index < customMainMarketsLimit &&
                <OutcomeList key={mainMarketId} mainMarketId={mainMarketId} sliceKey={sliceKey}/>
            )
        ))
    }

    render(): React.ReactNode {
        const { icons: { iconIsCollapsed, iconIsExpanded }, isExpanded, toggle, groupData, showMarketInMatchListGroupHeader, filteredMainMarketsList, isVirtual } = this.props;
        const { parentId: tournamentId, _type } = groupData as FilteredMatchesWithEvents;

        return (
            <div className={bem('Header')}>
                <span className={bem('Title')}>
                    {this.getTitleNode()}
                </span>
                {showMarketInMatchListGroupHeader && filteredMainMarketsList.length > 0 &&
                    <span className={bem('OutcomesList')}>
                        {this.outcomesContent(_type)}
                    </span>
                }
                <button className={bem('CollapseBtn')} onClick={toggle}>
                    <GeneralIcon name={isExpanded ? iconIsExpanded : iconIsCollapsed}/>
                </button>
                {!isVirtual && <FavoriteTournamentsButton tournamentId={tournamentId}/>}
            </div>
        );
    }
}

const MatchListGroupHeader: React.ComponentType<MatchListGroupHeaderPublicProps> = connect<MatchListGroupHeaderStoreProps>(
    (state: AppState, { sliceKey }: MatchListGroupHeaderPublicProps): MatchListGroupHeaderStoreProps => {
        const slice: MatchListStoreModel | undefined = getSlice<MatchListStoreModel>(state, sliceKey);

        if (!slice) {
            return {
                discipline: undefined,
            };
        }

        return ({
            discipline: slice.discipline,
        });
    },
)(MatchListGroupHeaderComponent);
export default MatchListGroupHeader;
